// src/components/PublishSuccessModal.js
// Celebratory modal shown after a successful publish.
// Displays the live URL with copy/visit/change-URL actions.

import React, { useState } from 'react';
import { CheckCircle2, Copy, ExternalLink, Pencil } from 'lucide-react';

function getPublicUrl(slug) {
  if (!slug) return null;
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${window.location.origin}/site/${slug}`;
  }
  return `https://divineframeworks.com/site/${slug}`;
}

/**
 * Props:
 *   isOpen: boolean
 *   onClose: () => void
 *   slug: string | null
 *   onChangeUrl: () => void | undefined — if provided, shows "Change URL" link
 *   firestoreOk: boolean — true (default) = Firestore write succeeded; false = show retry warning
 */
const PublishSuccessModal = ({ isOpen, onClose, slug, onChangeUrl, firestoreOk = true }) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const siteUrl = getPublicUrl(slug);

  const handleVisit = () => {
    if (siteUrl) window.open(siteUrl, '_blank', 'noopener,noreferrer');
  };

  const handleCopy = async () => {
    if (!siteUrl) return;
    try {
      await navigator.clipboard.writeText(siteUrl);
    } catch {
      // Fallback for older browsers
      const el = document.createElement('textarea');
      el.value = siteUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        zIndex: 100,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          maxWidth: '440px',
          width: '100%',
          padding: '36px 32px 28px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
          textAlign: 'center',
        }}
      >
        {/* Icon */}
        <div style={{ marginBottom: '20px' }}>
          <CheckCircle2
            size={52}
            style={{ color: '#16a34a', margin: '0 auto', display: 'block' }}
          />
        </div>

        {/* Title */}
        <h2
          style={{
            fontSize: '20px',
            fontWeight: 700,
            color: '#111827',
            marginBottom: '8px',
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          Your site is live! 🎉
        </h2>

        {/* Subtitle */}
        <p
          style={{
            fontSize: '14px',
            color: '#6B7280',
            marginBottom: firestoreOk ? '24px' : '12px',
            lineHeight: '1.5',
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          Your site is now accessible to anyone with the link.
        </p>

        {/* Firestore sync warning — shown when Firestore write failed */}
        {!firestoreOk && (
          <div
            style={{
              backgroundColor: '#FFFBEB',
              border: '1px solid #FDE68A',
              borderRadius: '8px',
              padding: '10px 14px',
              marginBottom: '16px',
              fontSize: '13px',
              color: '#92400E',
              lineHeight: '1.5',
              fontFamily: 'system-ui, sans-serif',
              textAlign: 'left',
            }}
          >
            ⚠️ Your site was saved locally but may take a moment to appear publicly. If the live URL shows old content, try publishing again.
          </div>
        )}

        {/* URL display box — clickable to open site */}
        {siteUrl && (
          <div style={{ marginBottom: onChangeUrl ? '8px' : '24px' }}>
            <div
              role="link"
              tabIndex={0}
              onClick={handleVisit}
              onKeyDown={(e) => { if (e.key === 'Enter') handleVisit(); }}
              style={{
                backgroundColor: '#F5F3FF',
                border: '1px solid #DDD6FE',
                borderRadius: '10px',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                flexWrap: 'wrap',
                cursor: 'pointer',
                transition: 'background-color 0.15s ease',
                userSelect: 'none',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#EDE9FE'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#F5F3FF'; }}
            >
              <span style={{ fontSize: '13px', color: '#6B7280', fontFamily: 'ui-monospace, monospace, system-ui' }}>
                divineframeworks.com/site/
              </span>
              <span style={{ fontSize: '13px', fontWeight: 700, color: '#7c3aed', fontFamily: 'ui-monospace, monospace, system-ui' }}>
                {slug}
              </span>
              <ExternalLink size={12} style={{ color: '#9CA3AF', flexShrink: 0 }} />
            </div>

            {/* Change URL link */}
            {onChangeUrl && (
              <div style={{ marginTop: '8px', marginBottom: '16px' }}>
                <button
                  type="button"
                  onClick={onChangeUrl}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '12px',
                    color: '#9CA3AF',
                    cursor: 'pointer',
                    fontFamily: 'system-ui, sans-serif',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '2px 4px',
                    transition: 'color 0.15s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#7c3aed'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = '#9CA3AF'; }}
                >
                  <Pencil size={11} />
                  Change URL
                </button>
              </div>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div
          style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '20px',
          }}
        >
          {/* Open Site */}
          <button
            type="button"
            onClick={handleVisit}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              padding: '10px 16px',
              backgroundColor: '#7c3aed',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'system-ui, sans-serif',
              transition: 'background-color 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#6D28D9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#7c3aed'; }}
          >
            <ExternalLink size={15} />
            Open Site
          </button>

          {/* Copy Link */}
          <button
            type="button"
            onClick={handleCopy}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              padding: '10px 16px',
              backgroundColor: 'white',
              color: copied ? '#16a34a' : '#7c3aed',
              border: `1.5px solid ${copied ? '#16a34a' : '#7c3aed'}`,
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'system-ui, sans-serif',
              transition: 'all 0.15s ease',
            }}
          >
            <Copy size={15} />
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
        </div>

        {/* Close link */}
        <button
          type="button"
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '13px',
            color: '#9CA3AF',
            cursor: 'pointer',
            fontFamily: 'system-ui, sans-serif',
            transition: 'color 0.15s ease',
            padding: '4px 8px',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#6B7280'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#9CA3AF'; }}
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default PublishSuccessModal;
